import React, { useEffect, useState, createContext, useContext } from 'react';
import { Modal } from '../components/ui/Modal';
import { renderCurrencyLanguage } from '../utils';

export const DataContext = createContext();

export function DataWrapper({ children }) {
  const [myCards, setMyCards] = useState({});
  const [checkoutItems, setCheckOutItems] = useState([]);
  // Hydrate the profile synchronously from localStorage so the auth token is
  // available on first render, letting mount-time menu fetches send it right away
  // (so HeyWaitr+ member pricing survives a hard reload).
  const [userProfile, setUserProfile] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user_profile')) || {};
    } catch (e) {
      return {};
    }
  });
  const [orderFromRestaurantsNotAllowed, setOrderFromRestaurantsNotAllowed] = useState(false);
  const [language, setLanguage] = useState('en');
  const [restaurantLanguageOverride, setRestaurantLanguageOverride] = useState(null);

  // Get the effective language considering restaurant override
  const getEffectiveLanguage = () => {
    return restaurantLanguageOverride || language;
  };

  // Set language based on restaurant currency (only if no user_lang exists)
  const setLanguageFromRestaurant = (restaurant, language) => {
    if (language) {
      setRestaurantLanguageOverride(language);

      return;
    }

    const hasUserLang = localStorage.getItem('user_lang');
    if (!hasUserLang && restaurant?.currency?.code) {
      const restaurantLang = renderCurrencyLanguage(restaurant.currency.code);
      setRestaurantLanguageOverride(restaurantLang);
    }
  };

  // Clear restaurant language override
  const clearRestaurantLanguageOverride = () => {
    setRestaurantLanguageOverride(null);
  };

  useEffect(() => {
    // Initialize language from localStorage or default to 'en'.
    // (The user profile is hydrated synchronously in the useState initializer above.)
    const savedLanguage = localStorage.getItem('user_lang');
    setLanguage(savedLanguage || 'en');
  }, []);

  return (
    <DataContext.Provider
      value={{
        myCards,
        setMyCards,
        checkoutItems,
        setCheckOutItems,
        userProfile,
        setUserProfile,
        language: getEffectiveLanguage(),
        setLanguage,
        orderFromRestaurantsNotAllowed,
        setOrderFromRestaurantsNotAllowed,
        setLanguageFromRestaurant,
        clearRestaurantLanguageOverride,
      }}
    >
      {children}

      {/* Order Not Allowed Modal */}
      <Modal
        isOpen={orderFromRestaurantsNotAllowed}
        onClose={() => setOrderFromRestaurantsNotAllowed(false)}
      >
        <div className="text-center space-y-4">
          <h4 className="text-lg sm:text-xl">Online and app orders are temporarily closed</h4>
          <p className="text-sm sm:text-base">
            We are not accepting online and app orders right now, but if you get hold of one of the
            employees, they will be happy to assist you.
          </p>
          <button
            className="w-full sm:w-[200px] bg-primary text-sm text-white font-bold px-4 py-[12px] rounded-full hover:bg-secondary transition-colors"
            onClick={() => setOrderFromRestaurantsNotAllowed(false)}
          >
            Close
          </button>
        </div>
      </Modal>
    </DataContext.Provider>
  );
}

export function useDataContext() {
  return useContext(DataContext);
}
